import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { useKleurenOrdersList, useMarkPrinted } from "@/hooks/useKleurenOrders";
import KleurenOrdersFilters from "@/components/kleuren-orders/KleurenOrdersFilters";
import KleurenOrdersTable from "@/components/kleuren-orders/KleurenOrdersTable";
import KleurenOrderDetailSheet from "@/components/kleuren-orders/KleurenOrderDetailSheet";
import { useDebounce } from "@/hooks/use-debounce";

const KleurenOrders = () => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("open");
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [detailOrder, setDetailOrder] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading } = useKleurenOrdersList({
    search: debouncedSearch || undefined,
    status: status === "all" ? undefined : status,
  });

  const markPrinted = useMarkPrinted();

  const orders = data?.data || [];

  const handleToggleSelect = useCallback((ordernumber: string) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (next.has(ordernumber)) next.delete(ordernumber);
      else next.add(ordernumber);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((selected: boolean) => {
    if (selected) {
      setSelectedOrders(new Set(orders.map(o => o.ordernumber)));
    } else {
      setSelectedOrders(new Set());
    }
  }, [orders]);

  const handleMarkPrinted = () => {
    if (selectedOrders.size === 0) return;
    markPrinted.mutate([...selectedOrders], {
      onSuccess: () => setSelectedOrders(new Set()),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Kleuren Orders</h1>
          {data?.meta && (
            <p className="text-sm text-muted-foreground">{data.meta.total} orders totaal</p>
          )}
        </div>
        <Button
          onClick={handleMarkPrinted}
          disabled={selectedOrders.size === 0 || markPrinted.isPending}
        >
          <Printer className="h-4 w-4 mr-2" />
          Afdrukken ({selectedOrders.size})
        </Button>
      </div>

      <KleurenOrdersFilters
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={setStatus}
      />

      <KleurenOrdersTable
        orders={orders}
        isLoading={isLoading}
        selectedOrders={selectedOrders}
        onToggleSelect={handleToggleSelect}
        onSelectAll={handleSelectAll}
        onOrderClick={setDetailOrder}
        status={status}
        onStatusChange={setStatus}
      />

      <KleurenOrderDetailSheet
        ordernumber={detailOrder}
        open={!!detailOrder}
        onOpenChange={(open) => { if (!open) setDetailOrder(null); }}
      />
    </div>
  );
};

export default KleurenOrders;
